// src/index.js
import React from "react";
import {createRoot} from "react-dom/client";
import "./styles/bootstrap.scss";
import "./index.css";
import "./styles/big-calendar-fix.css";

import App from "./App";
import reportWebVitals from "./reportWebVitals";
import GlobalFont from "./components/GlobalFont.tsx";

import {TrackGalleryProvider} from "./context/TrackGalleryContext";
import {AlertProvider} from "./context/AlertContext";
import {LoadingProvider} from "./context/LoadingProvider";

import {authReady} from "./authBootstrap";
import {appCheckReady} from "./firebase";

/**
 * Build/version cache-buster.
 * - Forces Safari (and any browser) to refresh when a new deploy lands.
 * - Clears CacheStorage (not your auth/session by default).
 */
const BUILD_ID = process.env.REACT_APP_BUILD_ID || "dev";

async function purgeCachesOnNewBuild() {
  try {
    const key = "vonthekeyz_build_id";
    const prev = localStorage.getItem(key);

    // Store on first run
    if (!prev) {
      localStorage.setItem(key, BUILD_ID);
      return;
    }

    // Same build: do nothing
    if (prev === BUILD_ID) return;

    // New build detected
    localStorage.setItem(key, BUILD_ID);

    // Unregister any old SWs if they exist (even if you don't see one)
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }

    // Clear CacheStorage (this is what can keep old app shells/assets around)
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }

    // Reload with a cache-busting query param
    const url = new URL(window.location.href);
    url.searchParams.set("v", BUILD_ID);
    window.location.replace(url.toString());
  } catch (e) {
    // Never block app boot
    // eslint-disable-next-line no-console
    console.warn("purgeCachesOnNewBuild skipped:", e);
  }
}

// Run BEFORE mounting React
purgeCachesOnNewBuild();

/*
 * Mount only after auth and the initial App Check exchange have settled.
 * This prevents the first protected Firestore listener from racing the
 * Enterprise token exchange. appCheckReady is bounded and remains fail-open.
 */
const bootTimeout = new Promise((resolve) => setTimeout(resolve, 12000));
Promise.race([Promise.allSettled([authReady, appCheckReady]), bootTimeout]).finally(() => {
  createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <LoadingProvider
        autoTrackFetch={false}
        spinnerColor="#21d7c5"
        spinnerTrack="rgba(33,215,197,.25)"
        labelColor="gold"
        overlayBg="rgba(0,0,0,.28)"
      >
        <AlertProvider>
          <GlobalFont />
          <TrackGalleryProvider>
            <App />
          </TrackGalleryProvider>
        </AlertProvider>
      </LoadingProvider>
    </React.StrictMode>
  );
});

reportWebVitals();
